import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

// Import before/after images
import houseBefore from "@/assets/before-after/house-before.webp";
import houseAfter from "@/assets/before-after/house-after.webp";
import pathwayBefore from "@/assets/before-after/pathway-before.webp";
import pathwayAfter from "@/assets/before-after/pathway-after.webp";
import fenceBefore from "@/assets/before-after/fence-before.webp";
import fenceAfter from "@/assets/before-after/fence-after.webp";

const beforeAfterImages = [
  { before: houseBefore, after: houseAfter, caption: "House Washing" },
  { before: pathwayBefore, after: pathwayAfter, caption: "Brick Pathway Cleaning" },
  { before: fenceBefore, after: fenceAfter, caption: "Fence Restoration" },
];

interface BeforeAfterSliderProps {
  before: string;
  after: string;
  caption: string;
}

const BeforeAfterSlider = ({ before, after, caption }: BeforeAfterSliderProps) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => { isDragging.current = true; };
  const handleMouseUp = () => { isDragging.current = false; };
  const handleMouseMove = (e: React.MouseEvent) => { if (isDragging.current) handleMove(e.clientX); };
  const handleTouchMove = (e: React.TouchEvent) => { handleMove(e.touches[0].clientX); };

  useEffect(() => {
    const handleGlobalMouseUp = () => { isDragging.current = false; };
    window.addEventListener("mouseup", handleGlobalMouseUp);
    return () => window.removeEventListener("mouseup", handleGlobalMouseUp);
  }, []);

  return (
    <div className="relative">
      <div
        ref={containerRef}
        className="relative aspect-[4/3] rounded-xl overflow-hidden cursor-ew-resize select-none touch-none"
        role="slider"
        aria-label="Drag to compare before and after"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(sliderPosition)}
        tabIndex={0}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
      >
        <img src={after} alt="After cleaning" width={800} height={600} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <img src={before} alt="Before cleaning" width={800} height={600} loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }} />

        <div className="absolute top-4 left-4 bg-secondary/90 backdrop-blur-sm text-white px-6 py-3 rounded-lg text-2xl font-bold shadow-lg border-2 border-white/20">BEFORE</div>
        <div className="absolute top-4 right-4 bg-primary/90 backdrop-blur-sm text-white px-6 py-3 rounded-lg text-2xl font-bold shadow-lg border-2 border-white/20">AFTER</div>

        <div className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize" style={{ left: `${sliderPosition}%`, transform: "translateX(-50%)" }}>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center">
            <div className="flex gap-0">
              <ChevronLeft className="w-5 h-5 text-secondary" />
              <ChevronRight className="w-5 h-5 text-secondary" />
            </div>
          </div>
        </div>
      </div>
      <p className="text-center text-white font-medium text-lg mt-4">{caption}</p>
    </div>
  );
};

const BeforeAfterSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % beforeAfterImages.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + beforeAfterImages.length) % beforeAfterImages.length);

  return (
    <section className="section-padding bg-secondary">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold font-heading text-white mb-4">
            TRANSFORMATIONS THAT{" "}
            <span className="text-primary">SPEAK FOR THEMSELVES</span>
          </h2>
          <p className="text-lg text-gray-300">Drag the slider to see the dramatic difference</p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          <BeforeAfterSlider {...beforeAfterImages[currentSlide]} />
          <Button variant="outline" size="icon" className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border-0 shadow-lg z-10" onClick={prevSlide} aria-label="Previous slide">
            <ChevronLeft className="w-6 h-6" />
          </Button>
          <Button variant="outline" size="icon" className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border-0 shadow-lg z-10" onClick={nextSlide} aria-label="Next slide">
            <ChevronRight className="w-6 h-6" />
          </Button>
          <div className="flex justify-center gap-2 mt-8">
            {beforeAfterImages.map((_, index) => (
              <button
                key={index}
                className={`min-w-[48px] min-h-[48px] flex items-center justify-center ${index === currentSlide ? "" : ""}`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                aria-current={index === currentSlide ? "true" : undefined}
              >
                <span className={`block rounded-full transition-all ${index === currentSlide ? "bg-primary w-8 h-3" : "bg-white/30 hover:bg-white/50 w-3 h-3"}`} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterSection;